import { toast } from "@akaike/ui";

/** Qcom's toast vocabulary. Kept as-is: 145 call sites read `notify(msg, "crit")`,
 *  which says what the message means without naming a toast library method. */
type Kind = "ok" | "crit" | "info";

const EMIT: Record<Kind, (message: string) => void> = {
  ok: (message) => toast.success(message),
  crit: (message) => toast.error(message),
  info: (message) => toast.info(message),
};

/** One reference, shared by every caller for the life of the app.
 *
 * Module scope rather than a closure built per render, because the identity is part of the
 * contract: callers put it in dependency lists, and a fresh function each render turns the
 * first `useEffect(..., [notify])` somebody writes into an every-render effect with
 * nothing on screen to explain it. Nothing here closes over render state, so there is
 * nothing to capture.
 */
const notify = (message: string, kind: Kind = "ok") => EMIT[kind](message);

/**
 * Raises a toast on the shared Toaster.
 *
 * A hook returning a `(message, kind?)` function, which is the shape all 145 call sites
 * read - but there is no provider and no context behind it. Qcom had a hand-rolled stack
 * that reimplemented what @akaike/ui already ships and Qtrack already uses, down to its
 * own fixed positioning, enter animation and auto-dismiss timer.
 */
export function useToast() {
  return notify;
}
